import { useCallback, useEffect } from 'react'

import { logger } from '@/utils/logger'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const log = logger.child({ file: 'src/hooks/usePresentationSync.ts' })

interface UsePresentationSyncProps {
  currentSlide: number
  setCurrentSlide: (slide: number) => void
  setSyncStatus: (status: 'synced' | 'syncing' | 'error') => void
  setAudienceConnected: (connected: boolean) => void
  audienceConnected: boolean
  totalSlides: number
}

export const usePresentationSync = ({
  currentSlide: _currentSlide, // available for future use and debugging
  setCurrentSlide,
  setSyncStatus,
  setAudienceConnected,
  audienceConnected,
  totalSlides,
}: UsePresentationSyncProps) => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  // Update audience view when slide changes
  const updateAudienceView = useCallback(
    (slideNumber: number) => {
      // If we have an opener window (the audience view), update it
      if (window.opener && !window.opener.closed) {
        setSyncStatus('syncing')
        log.info(
          { slideNumber },
          '🔄 Presenter: Sending slide change to audience',
        )
        try {
          window.opener.postMessage(
            {
              type: 'slide-change',
              slide: slideNumber,
            },
            '*',
          )
          log.info('✅ Presenter: Successfully sent message to audience')
          // Show synced status briefly
          setTimeout(() => setSyncStatus('synced'), 500)
        } catch (error) {
          log.error({ error }, '❌ Presenter: Could not update audience view')
          setSyncStatus('error')
          setTimeout(() => setSyncStatus('synced'), 2000)
        }
      } else {
        log.warn('⚠️ Presenter: No audience window found to update')
      }
    },
    [setSyncStatus],
  )

  // Sync language changes with audience view
  const syncLanguageWithAudience = useCallback((language: string) => {
    if (window.opener && !window.opener.closed) {
      log.info(
        { language },
        '🔄 Presenter: Sending language change to audience',
      )
      try {
        window.opener.postMessage(
          {
            type: 'language-change',
            language: language,
          },
          '*',
        )
        log.info('✅ Presenter: Successfully sent language change to audience')
      } catch (error) {
        log.error(
          { error },
          '❌ Presenter: Could not sync language with audience',
        )
      }
    }
  }, [])

  // Sync language changes with audience view
  useEffect(() => {
    if (audienceConnected) {
      syncLanguageWithAudience(i18n.language)
    }
  }, [i18n.language, audienceConnected, syncLanguageWithAudience])

  // Listen for messages from audience view
  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    const handleMessage = async (event: MessageEvent) => {
      log.info({ data: event.data }, '📨 Presenter: Received message')
      if (event.data.type === 'slide-change') {
        const slideNumber = event.data.slide
        log.info({ slideNumber }, '🔄 Presenter: Updating slide to')
        if (slideNumber >= 1 && slideNumber <= totalSlides) {
          setCurrentSlide(slideNumber)
          setSyncStatus('synced')
          setAudienceConnected(true)
          log.info(
            { slideNumber },
            '✅ Presenter: Successfully updated slide to',
          )
        } else {
          log.warn({ slideNumber }, '❌ Presenter: Invalid slide number')
        }
      } else if (event.data.type === 'language-change') {
        const language = event.data.language
        log.info(
          { language },
          '🔄 Presenter: Received language change from audience',
        )
        if (language && i18n.language !== language) {
          i18n.changeLanguage(language)
          log.info('✅ Presenter: Successfully changed language to', language)
        }
      } else if (event.data.type === 'presentation-stop') {
        log.info('🛑 Presenter: Presentation stopped by audience')
        // wait till navigated to an unauthenticated route to not trigger any auth functions
        await navigate({
          to: '/login',
          replace: true,
        })
        // close the presenter window
        window.close()
      }
    }

    window.addEventListener('message', handleMessage, { signal })
    return () => {
      abortController.abort()
    }
  }, [i18n, setCurrentSlide, setSyncStatus, setAudienceConnected, totalSlides])

  return {
    updateAudienceView,
    syncLanguageWithAudience,
  }
}
